import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ExpectedAnswerFormValue } from '@artem-lex/common-server/evals/legislationApplicability'
import { apiClient } from '@/shared/api/apiClient'

export const EVAL_TASKS_QUERY_KEY = 'evalTasks'
export const EVAL_TASK_DIRECTORY_QUERY_KEY = 'evalTaskDirectory'
export const EVAL_PRODUCTS_QUERY_KEY = 'evalProducts'
export const EVAL_PRODUCT_SOURCES_QUERY_KEY = 'evalProductSources'
export const EVAL_MACHINERY_QUESTIONS_QUERY_KEY = 'evalMachineryQuestions'
export const EVAL_ROHS_QUESTIONS_QUERY_KEY = 'evalRohsQuestions'
export const EVAL_EMC_QUESTIONS_QUERY_KEY = 'evalEmcQuestions'
export const EVAL_RED_QUESTIONS_QUERY_KEY = 'evalRedQuestions'
export const EVAL_WEEE_QUESTIONS_QUERY_KEY = 'evalWeeeQuestions'
export const EVAL_FGAS_QUESTIONS_QUERY_KEY = 'evalFgasQuestions'
export const EVAL_PED_QUESTIONS_QUERY_KEY = 'evalPedQuestions'
export const EVAL_SETTINGS_QUERY_KEY = 'evalSettings'

export type EvalTaskStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type EvalTaskType =
  | 'REPRESENTATIVE'
  | 'INSUFFICIENT_EVIDENCE'
  | 'AMBIGUOUS'
  | 'DIFFICULT_BOUNDARY'
  | 'HISTORICAL_FAILURE'
  | 'LANGUAGE_OR_DOCUMENT_SHAPE'
export type EvalDatasetSplit = 'DEVELOPMENT' | 'HELD_OUT'
export type EvalCriticality = 'NORMAL' | 'CRITICAL'

export interface EvalRequiredFact {
  id: string
  statement: string
  critical: boolean
  evidenceIds: string[]
}

export interface EvalForbiddenClaim {
  id: string
  statement: string
}

export interface EvalRubricInstanceDimension {
  id: string
  grader: 'CODE' | 'HUMAN' | 'MODEL'
  role: string
  advisory: boolean
  releaseGateId: string | null
  applicable: boolean
}

export interface EvalRubricInstance {
  rubricVersion: string
  policyVersion: string
  dimensions: EvalRubricInstanceDimension[]
}

export interface EvalEvidenceReference {
  productSourceId: string
  sourceNameSnapshot: string
  page: number | null
  section: string | null
  supportingText: string
}

export interface EvalTaskVersion {
  id: string
  taskKey: string
  version: number
  status: EvalTaskStatus
  suiteKey: string
  productId: string
  productName?: string | null
  productSku?: string | null
  productDescription?: string | null
  organizationId: string
  lawKey: string
  packVersion: number
  questionKey: string
  questionTextSnapshot: string
  questionFingerprint: string
  taskType: EvalTaskType
  datasetSplit: EvalDatasetSplit
  expectedAnswer: boolean | null
  expectedHasGap: boolean
  expectedLackOfEvidence: boolean
  requiredRationaleFacts: EvalRequiredFact[]
  forbiddenClaims: EvalForbiddenClaim[]
  evidenceReferences: EvalEvidenceReference[]
  criticality: EvalCriticality
  reviewNotes: string | null
  needsReview: boolean
  createdByUserId: string
  reviewedByUserId: string | null
  reviewedAt: string | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
  rubricInstance?: EvalRubricInstance
  canUnarchive?: boolean
}

export interface EvalProductListItem {
  id: string
  name: string
  sku: string | null
  organizationId: string
  productModel: string | null
}

export interface EvalProductSource {
  id: string
  name: string
  ingestionStatus: string
  type: string
}

export interface EvalMachineryQuestion {
  questionKey: string
  label: string
  helper: string | null
  fingerprint: string
}

export type LegislationEvalLawKey =
  | 'machinery'
  | 'lvd'
  | 'rohs'
  | 'emc'
  | 'red'
  | 'weee'
  | 'fgas'
  | 'ped'
export type EvalAuthoringLaw = LegislationEvalLawKey

export interface EvalTaskDraftInput {
  law?: EvalAuthoringLaw
  productId: string
  lawKey?: LegislationEvalLawKey
  questionKey: string
  taskType: EvalTaskType
  datasetSplit: EvalDatasetSplit
  expectedAnswerForm: ExpectedAnswerFormValue
  requiredRationaleFacts?: EvalRequiredFact[]
  forbiddenClaims?: EvalForbiddenClaim[]
  evidenceReferences?: EvalEvidenceReference[]
  criticality?: EvalCriticality
  reviewNotes?: string | null
}

export type EvalDirectorySuiteSlug =
  | 'legislation-applicability'
  | 'standards-extraction'

export interface EvalTaskDirectoryItem {
  suiteKey:
    | 'legislation-applicability-machinery-v1'
    | 'legislation-applicability-lvd-v1'
    | 'legislation-applicability-rohs-v1'
    | 'legislation-applicability-emc-v1'
    | 'legislation-applicability-red-v1'
    | 'legislation-applicability-weee-v1'
    | 'legislation-applicability-fgas-v1'
    | 'legislation-applicability-ped-v1'
    | 'standards-extraction'
  href: string
  subjectLabel: string
  promptLabel: string
  kindLabel: string
  status: EvalTaskStatus
  needsReview?: boolean
  datasetSplit: EvalDatasetSplit | null
  createdAt: string
  lawKey?: LegislationEvalLawKey
  updatedAt: string
}

export function useEvalTasksList(
  filters?: {
    status?: EvalTaskStatus
    productId?: string
    datasetSplit?: EvalDatasetSplit
    suiteKey?: string
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_TASKS_QUERY_KEY, 'list', filters ?? {}],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{ items: EvalTaskVersion[] }>(
        '/admin/evals/tasks',
        { params: filters }
      )
      return response.data
    },
  })
}

export function useEvalTaskDirectory(
  filters?: {
    status?: EvalTaskStatus
    suite?: EvalDirectorySuiteSlug
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY, filters ?? {}],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{ items: EvalTaskDirectoryItem[] }>(
        '/admin/evals/task-directory',
        { params: filters }
      )
      return response.data
    },
  })
}

export function useEvalTask(
  taskVersionId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_TASKS_QUERY_KEY, 'detail', taskVersionId],
    enabled: (options?.enabled ?? true) && Boolean(taskVersionId),
    queryFn: async () => {
      const response = await apiClient.get<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}`
      )
      return response.data
    },
  })
}

export function useSearchEvalProducts(
  searchQuery?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_PRODUCTS_QUERY_KEY, searchQuery ?? ''],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{ items: EvalProductListItem[] }>(
        '/admin/evals/products',
        { params: searchQuery ? { q: searchQuery } : undefined }
      )
      return response.data
    },
  })
}

export function useEvalProductSources(
  productId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_PRODUCT_SOURCES_QUERY_KEY, productId],
    enabled: (options?.enabled ?? true) && Boolean(productId),
    queryFn: async () => {
      const response = await apiClient.get<{ items: EvalProductSource[] }>(
        `/admin/evals/products/${productId}/sources`
      )
      return response.data
    },
  })
}

export interface EvalEvaluationTenant {
  organizationId: string | null
  organizationName: string | null
  questionModelId: string | null
  extractionModelId: string | null
  chatModelOptions: string[]
}

type UpdateEvalSettingsInput = {
  organizationId: string
  questionModelId?: string | null
  extractionModelId?: string | null
}

export function useEvalSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_SETTINGS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<EvalEvaluationTenant>(
        '/admin/evals/settings'
      )
      return response.data
    },
  })
}

export function useUpdateEvalSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateEvalSettingsInput) => {
      const body: UpdateEvalSettingsInput = {
        organizationId: input.organizationId,
      }
      if (input.questionModelId !== undefined) {
        body.questionModelId = input.questionModelId
      }
      if (input.extractionModelId !== undefined) {
        body.extractionModelId = input.extractionModelId
      }
      const response = await apiClient.put<EvalEvaluationTenant>(
        '/admin/evals/settings',
        body
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_SETTINGS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_PRODUCTS_QUERY_KEY],
      })
    },
  })
}

export function useEvalMachineryQuestions(options?: { enabled?: boolean }) {
  return useEvalLegislationQuestions('machinery', options)
}

export function useEvalLegislationQuestions(
  law: LegislationEvalLawKey,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_MACHINERY_QUESTIONS_QUERY_KEY, law],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/legislation-questions', { params: { law } })
      return response.data
    },
  })
}

export function useEvalEmcQuestions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_EMC_QUESTIONS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/emc-questions')
      return response.data
    },
  })
}

export function useEvalRohsQuestions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_ROHS_QUESTIONS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/rohs-questions')
      return response.data
    },
  })
}

export function useEvalRedQuestions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_RED_QUESTIONS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/red-questions')
      return response.data
    },
  })
}

export function useEvalFgasQuestions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_FGAS_QUESTIONS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/fgas-questions')
      return response.data
    },
  })
}

export function useEvalPedQuestions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_PED_QUESTIONS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/ped-questions')
      return response.data
    },
  })
}

export function useEvalWeeeQuestions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_WEEE_QUESTIONS_QUERY_KEY],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        packVersion: number
        questions: EvalMachineryQuestion[]
      }>('/admin/evals/weee-questions')
      return response.data
    },
  })
}

export interface EvalBulkCreateResult {
  created: Array<{ id: string; questionKey: string }>
  skipped: Array<{ questionKey: string; reason: string }>
}

export function useBulkCreateEvalTasks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      law?: EvalAuthoringLaw
      productId: string
      lawKey?: LegislationEvalLawKey
      datasetSplit?: EvalDatasetSplit
    }) => {
      const response = await apiClient.post<EvalBulkCreateResult>(
        '/admin/evals/tasks/bulk',
        input
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useCreateFilledEvalTasks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      input: Omit<EvalTaskDraftInput, 'questionKey'> & {
        questionKeys: string[]
      }
    ) => {
      const response = await apiClient.post<EvalBulkCreateResult>(
        '/admin/evals/tasks/fan-out',
        input
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useCreateEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: EvalTaskDraftInput) => {
      const response = await apiClient.post<EvalTaskVersion>(
        '/admin/evals/tasks',
        input
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useUpdateEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      taskVersionId,
      input,
    }: {
      taskVersionId: string
      input: Partial<EvalTaskDraftInput>
    }) => {
      const response = await apiClient.patch<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}`,
        input
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function usePublishEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskVersionId: string) => {
      const response = await apiClient.post<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}/publish`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useReviseEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskVersionId: string) => {
      const response = await apiClient.post<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}/revisions`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

function invalidateEvalTaskQueries(
  queryClient: ReturnType<typeof useQueryClient>
) {
  void queryClient.invalidateQueries({ queryKey: [EVAL_TASKS_QUERY_KEY] })
  void queryClient.invalidateQueries({
    queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
  })
}

export function useDeleteEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskVersionId: string) => {
      const response = await apiClient.delete<{ id: string }>(
        `/admin/evals/tasks/${taskVersionId}`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateEvalTaskQueries(queryClient)
    },
  })
}

export function useArchiveEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskVersionId: string) => {
      const response = await apiClient.post<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}/archive`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateEvalTaskQueries(queryClient)
    },
  })
}

export function useUnarchiveEvalTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskVersionId: string) => {
      const response = await apiClient.post<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}/unarchive`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateEvalTaskQueries(queryClient)
    },
  })
}

export const EVAL_EXPERIMENTS_QUERY_KEY = 'evalExperiments'

export function useDeleteEvalExperiment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (experimentId: string) => {
      const response = await apiClient.delete<{ id: string }>(
        `/admin/evals/experiments/${experimentId}`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXPERIMENTS_QUERY_KEY],
      })
    },
  })
}

export function useSetEvalTaskNeedsReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      taskVersionId,
      needsReview,
    }: {
      taskVersionId: string
      needsReview: boolean
    }) => {
      const response = await apiClient.put<EvalTaskVersion>(
        `/admin/evals/tasks/${taskVersionId}/needs-review`,
        { needsReview }
      )
      return response.data
    },
    onSuccess: () => {
      invalidateEvalTaskQueries(queryClient)
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXPERIMENTS_QUERY_KEY],
      })
    },
  })
}

export const EVAL_TRIALS_QUERY_KEY = 'evalTrials'
export const EVAL_COMPARE_QUERY_KEY = 'evalCompare'

export type EvalExperimentKind = 'BASELINE' | 'CANDIDATE'
export type EvalExperimentStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
export type EvalReviewStatus = 'UNREVIEWED' | 'REVIEWED'
export type EvalFailureClassification =
  | 'agent'
  | 'task'
  | 'grader'
  | 'harness'
  | 'corpus'
  | 'environment'
export type EvalGradeResult = 'PASS' | 'FAIL' | 'UNKNOWN' | 'NOT_APPLICABLE'
export type EvalPairedComparisonLabel =
  | 'stable_pass'
  | 'stable_fail'
  | 'improved'
  | 'regressed'
  | 'changed'
  | 'unstable'
  | 'invalid'
  | 'missing'

export interface EvalExperimentSummary {
  id: string
  name: string
  kind: EvalExperimentKind
  status: EvalExperimentStatus
  suiteKey: string
  datasetSplit: EvalDatasetSplit
  attemptsPerTask: number
  baselineExperimentId: string | null
  candidateCount: number
  taskCount: number
  attemptCount: number
  criticalFailures: number
  reviewedCount: number
  reviewProgress: number | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  errorClass: string | null
  errorMessage: string | null
}

export type EvalExperimentMetrics = {
  releaseGatePassRate: number | null
  passAt1: number | null
  passHat3: number | null
  criticalErrorCount: Record<string, number>
  abstentionPassRate: number | null
}

export interface EvalExtractionTrialRow {
  id: string
  extractionTaskVersionId: string
  attemptNumber: number
  status: string
  passed: boolean | null
  taskKey: string
  extractionType: string
  standardReference: string
  label: string
}

export interface EvalExperimentDetail extends EvalExperimentSummary {
  excludedTaskCount: number
  silverByTask?: Record<string, string | null>
  metrics?: EvalExperimentMetrics | null
  trials: Array<{
    id: string
    taskVersionId: string
    attemptNumber: number
    status: string
    reviewStatus: EvalReviewStatus
    excludedFromScores: boolean
    needsReview: boolean
    productId: string
    productName: string | null
    taskKey: string
    questionKey: string
    questionTextSnapshot: string
    criticality: EvalCriticality
    expectedAnswer: boolean | null
    expectedHasGap: boolean
    expectedLackOfEvidence: boolean
    outcome: unknown
    exactMatch: EvalGradeResult | null
    criticalCodeFail: boolean
    startedAt: string
  }>
  extractionTrials?: EvalExtractionTrialRow[]
}

export interface EvalDisplayEvent {
  sequence: number
  observationId: string
  parentObservationId: string | null
  eventType: string
  observationType: string
  name: string
  startTime: string
  endTime: string
  input?: unknown
  output?: unknown
  model?: string
  provider?: string
  usageDetails?: unknown
  statusMessage?: string
  retrievedEvidence?: EvalEvidenceGroup['evidence']
}

export interface EvalEvidenceGroup {
  toolCallId: string
  toolName: string
  evidence: Array<{
    documentId: string
    chunkId: string
    page: number | null
    section: string
    admittedText: string
    query?: string
  }>
}

export interface EvalGrade {
  id?: string
  dimension: string
  graderType: 'CODE' | 'HUMAN' | 'MODEL'
  result: EvalGradeResult
  critical?: boolean
  reason: string
  scoreDataType?: string
  scoreValue?: unknown
  traceId?: string
}

export interface EvalTrialReview {
  id: string
  experimentId: string
  attemptNumber: number
  status: string
  reviewStatus: EvalReviewStatus
  failureClassification: EvalFailureClassification | null
  reviewNotes: string | null
  reviewedByUser: { id: string; email: string } | null
  reviewedAt: string | null
  errorClass: string | null
  errorMessage: string | null
  task: {
    id: string
    taskKey: string
    productId: string
    productName: string | null
    questionKey: string
    questionTextSnapshot: string
    taskType: EvalTaskType
    expectedAnswer: boolean | null
    expectedHasGap: boolean
    expectedLackOfEvidence: boolean
    requiredRationaleFacts: unknown
    criticality: EvalCriticality
  }
  outcome: unknown
  events: EvalDisplayEvent[]
  evidenceByToolCall: EvalEvidenceGroup[]
  codeGrades: EvalGrade[]
  humanGrades: EvalGrade[]
  rubricInstance?: EvalRubricInstance
  releaseGates?: Array<{
    id: string
    dimension: string
    result: string
    critical: boolean
  }>
  rawTranscript: unknown
}

export interface EvalTrialReviewInput {
  coverage?: { result: EvalGradeResult; reason: string }
  groundedness: { result: EvalGradeResult; reason: string }
  coherence: { result: EvalGradeResult; reason: string }
  reviewStatus: EvalReviewStatus
  failureClassification?: EvalFailureClassification | null
  reviewNotes?: string | null
}

export interface EvalComparisonReport {
  comparable: boolean
  mismatchedNonPromptKeys: string[]
  pairCount: number
  countedPairCount: number
  labelCounts: Record<EvalPairedComparisonLabel, number> | null
  pairs: Array<{
    taskVersionId: string
    attemptNumber: number
    productId: string | null
    taskKey: string | null
    label: EvalPairedComparisonLabel
    headlineLabel?: EvalPairedComparisonLabel
    outcomeChanged?: boolean
    rationaleChanged?: boolean
    retrievalChanged?: boolean
    baselineTrialId: string | null
    candidateTrialId: string | null
  }>
  byTask: Array<{
    key: string
    label: EvalPairedComparisonLabel
    taskKey: string | null
  }>
  byProduct: Array<{
    key: string
    label: EvalPairedComparisonLabel
    productId: string | null
  }>
  byDimension?: Array<{
    dimension: string
    grader: 'CODE' | 'HUMAN' | 'MODEL'
    advisory: boolean
    labelCounts: Record<EvalPairedComparisonLabel, number> | null
    byTask: Array<{ taskVersionId: string; label: EvalPairedComparisonLabel }>
  }>
  decision?: {
    id: string
    decision: 'ACCEPT' | 'REJECT'
    summary: string
    targetedDimensionIds: unknown
    evidenceTaskVersionIds: unknown
  } | null
  baselineMetrics?: EvalExperimentMetrics
  candidateMetrics?: EvalExperimentMetrics
}

export function useEvalExperimentsList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [EVAL_EXPERIMENTS_QUERY_KEY, 'list'],
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      const items = query.state.data?.items
      if (items == null) {
        return false
      }
      const inFlight = items.some(
        (item) => item.status === 'PENDING' || item.status === 'RUNNING'
      )
      if (inFlight) {
        return 5000
      }
      return false
    },
    queryFn: async () => {
      const response = await apiClient.get<{ items: EvalExperimentSummary[] }>(
        '/admin/evals/experiments'
      )
      return response.data
    },
  })
}

export function useEvalExperiment(
  experimentId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_EXPERIMENTS_QUERY_KEY, 'detail', experimentId],
    enabled: (options?.enabled ?? true) && Boolean(experimentId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'PENDING' || status === 'RUNNING') {
        return 5000
      }
      return false
    },
    queryFn: async () => {
      const response = await apiClient.get<EvalExperimentDetail>(
        `/admin/evals/experiments/${experimentId}`
      )
      return response.data
    },
  })
}

export type StartEvalExperimentInput = {
  name: string
  kind: EvalExperimentKind
  split: EvalDatasetSplit
  attempts: 1 | 3
  taskKeys: string[]
  baselineExperimentId?: string
  lawKey?: LegislationEvalLawKey
}

export type StartExtractionEvalExperimentInput = {
  name: string
  kind: EvalExperimentKind
  split: EvalDatasetSplit
  attempts: 1 | 3
  taskKeys: string[]
  baselineExperimentId?: string
}

export function useStartExtractionEvalExperiment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: StartExtractionEvalExperimentInput) => {
      const response = await apiClient.post<{ experimentId: string }>(
        '/admin/evals/extraction-experiments',
        input
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [EVAL_EXPERIMENTS_QUERY_KEY],
      })
    },
  })
}

export function useStartEvalExperiment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: StartEvalExperimentInput) => {
      const response = await apiClient.post<{ experimentId: string }>(
        '/admin/evals/experiments',
        input
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [EVAL_EXPERIMENTS_QUERY_KEY],
      })
    },
  })
}

export function useEvalTrial(
  trialId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_TRIALS_QUERY_KEY, trialId],
    enabled: (options?.enabled ?? true) && Boolean(trialId),
    queryFn: async () => {
      const response = await apiClient.get<EvalTrialReview>(
        `/admin/evals/trials/${trialId}`
      )
      return response.data
    },
  })
}

export function useSetEvalTrialTaskExclusion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      trialId,
      excludedFromScores,
    }: {
      trialId: string
      excludedFromScores: boolean
    }) => {
      const response = await apiClient.patch<EvalExperimentDetail>(
        `/admin/evals/trials/${trialId}/review`,
        { excludedFromScores }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXPERIMENTS_QUERY_KEY],
      })
    },
  })
}

export function useSubmitEvalTrialReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      trialId,
      input,
    }: {
      trialId: string
      input: EvalTrialReviewInput
    }) => {
      const response = await apiClient.put<EvalTrialReview>(
        `/admin/evals/trials/${trialId}/review`,
        input
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [EVAL_TRIALS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXPERIMENTS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TRIALS_QUERY_KEY, variables.trialId],
      })
    },
  })
}

export function useEvalComparison(
  baselineId: string | undefined,
  candidateId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_COMPARE_QUERY_KEY, baselineId, candidateId],
    enabled:
      (options?.enabled ?? true) && Boolean(baselineId) && Boolean(candidateId),
    queryFn: async () => {
      const response = await apiClient.get<EvalComparisonReport>(
        `/admin/evals/compare/${baselineId}/${candidateId}`
      )
      return response.data
    },
  })
}

export function useUpsertEvalComparisonDecision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      baselineId,
      candidateId,
      input,
    }: {
      baselineId: string
      candidateId: string
      input: {
        decision: 'ACCEPT' | 'REJECT'
        summary: string
        targetedDimensionIds?: string[]
        evidenceTaskVersionIds?: string[]
      }
    }) => {
      const response = await apiClient.put(
        `/admin/evals/compare/${baselineId}/${candidateId}/decision`,
        input
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          EVAL_COMPARE_QUERY_KEY,
          variables.baselineId,
          variables.candidateId,
        ],
      })
    },
  })
}
